// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toApiError } from "@/api/api-error";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

export type Firmware = components["schemas"]["FirmwareResponse"];
export type FirmwareAssignment = components["schemas"]["FirmwareAssignmentResponse"];
export type OtaDeviceType = components["schemas"]["OtaDeviceTypeEnum"];
export type OtaCpuType = components["schemas"]["OtaCpuTypeEnum"];
export type FirmwareSortBy = components["schemas"]["FirmwareSortByEnum"];
export type FirmwareAssignmentScope = components["schemas"]["FirmwareAssignmentScopeEnum"];
export type FirmwareAssignmentsSortBy = components["schemas"]["FirmwareAssignmentsSortByEnum"];

export interface FirmwareListResult {
  firmware: Firmware[];
  count: number;
}

interface FirmwareListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: FirmwareSortBy;
  orderBy?: "asc" | "desc";
  deviceType?: OtaDeviceType;
  cpuType?: OtaCpuType;
  onlyProduction?: boolean;
  onlyActive?: boolean;
}

export function useFirmwareListQuery(params: FirmwareListParams = {}) {
  const { page = 1, limit = 20, search, sortBy, orderBy, deviceType, cpuType, onlyProduction, onlyActive } = params;

  return useQuery({
    queryKey: [
      "firmware",
      "versions",
      { page, limit, search, sortBy, orderBy, deviceType, cpuType, onlyProduction, onlyActive },
    ],
    queryFn: async () => {
      const res = await api.GET("/v1/hardware/firmware/", {
        params: {
          query: {
            page,
            limit,
            search: search || undefined,
            sortBy,
            orderBy,
            "filter[deviceType]": deviceType,
            "filter[cpuType]": cpuType,
            "filter[onlyProduction]": onlyProduction,
            "filter[onlyActive]": onlyActive,
          },
        },
      });
      return res.data as unknown as FirmwareListResult;
    },
  });
}

export function useFirmwareQuery(firmwareId: number) {
  return useQuery({
    queryKey: ["firmware", "version", firmwareId],
    queryFn: async () => {
      const res = await api.GET("/v1/hardware/firmware/{firmware_id}", {
        params: { path: { firmware_id: firmwareId } },
      });
      if (res.error) throw new Error("Failed to load OTA version");
      return res.data as unknown as Firmware;
    },
    enabled: firmwareId > 0,
  });
}

interface FirmwareAssignmentsParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: FirmwareAssignmentsSortBy;
  orderBy?: "asc" | "desc";
  scope?: FirmwareAssignmentScope;
}

export function useFirmwareAssignmentsQuery(firmwareId: number, params: FirmwareAssignmentsParams = {}) {
  const { page = 1, limit = 20, search, sortBy, orderBy, scope } = params;
  return useQuery({
    queryKey: ["firmware", "assignments", firmwareId, { page, limit, search, sortBy, orderBy, scope }],
    queryFn: async () => {
      const res = await api.GET("/v1/hardware/firmware/{firmware_id}/assignments", {
        params: {
          path: { firmware_id: firmwareId },
          query: {
            page,
            limit,
            search: search || undefined,
            sortBy,
            orderBy,
            "filter[scope]": scope,
          },
        },
      });
      if (res.error) throw new Error("Failed to load OTA assignments");
      return res.data as unknown as components["schemas"]["FirmwareAssignmentsListResponse"];
    },
    enabled: firmwareId > 0,
  });
}

export interface CreateFirmwareInput {
  deviceType: OtaDeviceType;
  versionStr: string;
  versionNum: number;
  isProduction: boolean;
  changeLog?: string;
  cpuType?: OtaCpuType;
  file: File;
}

export function useCreateFirmwareMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateFirmwareInput) => {
      // multipart/form-data upload — pass the FormData straight through so the browser sets the
      // multipart boundary (and the auth/error middleware still applies).
      const fd = new FormData();
      fd.append("deviceType", input.deviceType);
      fd.append("versionStr", input.versionStr);
      fd.append("versionNum", String(input.versionNum));
      fd.append("isProduction", String(input.isProduction));
      if (input.changeLog) fd.append("changeLog", input.changeLog);
      if (input.cpuType) fd.append("cpuType", input.cpuType);
      fd.append("file", input.file);
      const res = await api.POST("/v1/hardware/firmware/", {
        body: fd as never,
        bodySerializer: () => fd,
      });
      // Keep the status + backend detail — the dialog routes a 409 to the version field.
      if (res.error) throw toApiError(res.error, res.response, "Failed to create OTA version");
      return res.data as unknown as components["schemas"]["FirmwareCreateResponse"];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["firmware"] });
    },
  });
}

export function useToggleFirmwareActiveMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { firmwareId: number; isActive: boolean }) => {
      const res = await api.PATCH("/v1/hardware/firmware/{firmware_id}/active", {
        params: { path: { firmware_id: params.firmwareId } },
        body: { isActive: params.isActive },
      });
      if (res.error) throw new Error("Failed to update OTA version status");
      return res.data as unknown as components["schemas"]["StatusResponse"];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["firmware"] });
    },
  });
}

export function useToggleFirmwareProductionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { firmwareId: number; isProduction: boolean }) => {
      const res = await api.PATCH("/v1/hardware/firmware/{firmware_id}/production", {
        params: { path: { firmware_id: params.firmwareId } },
        body: { isProduction: params.isProduction },
      });
      if (res.error) throw new Error("Failed to update OTA production status");
      return res.data as unknown as components["schemas"]["StatusResponse"];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["firmware"] });
    },
  });
}
